import React, { useEffect, useState, useCallback, useRef } from "react";

import {
  Box,
  Card,
  Stack,
  Typography,
  CircularProgress,
  Backdrop,
  Paper,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import EditIcon from "@mui/icons-material/Edit";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import DownloadIcon from "@mui/icons-material/Download";

import SidebarView, { SidebarRef } from "../common/Sidebar";
import { trackEvent } from "../../analytics";
import VideoPlayer from "./components/VideoPlayer";
import Subtitle from "./components/Subtitle";
import TextOverlayPanel from "./components/TextOverlayPanel";
import GifPicker from "./components/GifPicker";
import { AspectRatio, ASPECT_RATIOS } from "./components/AspectRatioSelector";
import {
  editShortVideoService,
  mapApiGifOverlayToFe,
  mapApiTextOverlayToFe,
} from "./services";
import Headerbar from "../common/Headerbar";
import { episodeService } from "../Episode/services";
import { GifOverlay, Paragraph, SubtitleStyles, TextOverlay } from "./types";
import { syncParagraphWordsFromText } from "./utils/syncParagraphWords";
import Timeline from "../Editor/Timeline";
import { IElement } from "../Editor/Timeline/components/Element/types/IElement";
import { WebSocketEventName } from "../common/enums/WebSocketEventName";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useExportDownload } from "../../hooks/useExportDownload";
import { useCanEdit } from "../../hooks/useCanEdit";
import Breadcrumbs from "../common/Breadcrumbs";

interface VideoControls {
  play: () => void;
  pause: () => void;
  seekTo: (time: number) => void;
  getDuration: () => number;
}

// Minimum on-timeline duration so a newly added/duplicated element is always
// visible and comfortably draggable (never collapsed to ~0 width at the end).
const MIN_OVERLAY_DURATION_MS = 2000;

/**
 * Keep an overlay's time range inside the video and at least
 * MIN_OVERLAY_DURATION_MS wide, so it can never end up "stuck" past the
 * end of the video where it can't be dragged.
 */
function clampOverlayTiming(
  startMs: number,
  endMs: number,
  durationMs: number,
): { startMs: number; endMs: number } {
  const safeDuration = Math.max(durationMs, MIN_OVERLAY_DURATION_MS);
  let start = Math.max(
    0,
    Math.min(startMs, safeDuration - MIN_OVERLAY_DURATION_MS),
  );
  let end = Math.min(Math.max(endMs, start), safeDuration);
  if (end - start < MIN_OVERLAY_DURATION_MS) {
    end = Math.min(safeDuration, start + MIN_OVERLAY_DURATION_MS);
    start = Math.max(0, end - MIN_OVERLAY_DURATION_MS);
  }
  return { startMs: start, endMs: end };
}

export default function EditShortVideo() {
  const { videoId, id: episodeId } = useParams();
  const navigate = useNavigate();
  // Viewers get a read-only editor: they can preview and export the video,
  // but editing panels are hidden and edits are never persisted.
  const canEdit = useCanEdit();
  const sidebarRef = useRef<SidebarRef>(null);
  const nextElementIdRef = useRef(1);
  const getNextElementId = useCallback(() => {
    const id = nextElementIdRef.current;
    nextElementIdRef.current += 1;
    return id;
  }, []);

  const {
    data: shortVideo,
    isLoading: isVideoLoading,
    isError: isVideoError,
  } = useQuery({
    queryKey: ["getVideo", videoId],
    queryFn: () => editShortVideoService.getVideo(videoId),
    enabled: !!videoId,
  });

  const { data: episode } = useQuery({
    queryKey: ["getEpisodeById", episodeId],
    queryFn: () => episodeService.getEpisodeById(episodeId),
    enabled: !!episodeId,
  });

  const episodeName = episode?.name || "Episode";
  const videoTitle = shortVideo?.title || "Video";

  const handleMobileMenuToggle = () => {
    sidebarRef.current?.toggleMobile();
  };

  const [elements, setElements] = useState<IElement[]>([]);
  const [currentTimeInMilliseconds, setCurrentTimeInMilliseconds] =
    useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [styles, setStyles] = useState<SubtitleStyles>(
    shortVideo?.subtitle_styles ?? {},
  );
  const [paragraphs, setParagraphs] = useState<Paragraph[]>([]);
  const [videoControlsRef, setVideoControlsRef] =
    useState<VideoControls | null>(null);
  const {
    isExporting,
    setIsExporting,
    exportError,
    setExportError,
    isExportingRef,
    downloadFile,
  } = useExportDownload();
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(
    ASPECT_RATIOS.find((r) => r.id === "standard") || ASPECT_RATIOS[0],
  );

  const [cropPositions, setCropPositions] = useState<
    Record<string, { x: number; y: number }>
  >({});

  const currentCrop = cropPositions[aspectRatio.id] ?? { x: 50, y: 50 };

  const saveCropTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCropPositionChange = useCallback(
    (x: number, y: number) => {
      setCropPositions((prev) => {
        const next = { ...prev, [aspectRatio.id]: { x, y } };
        if (saveCropTimerRef.current) {
          clearTimeout(saveCropTimerRef.current);
        }
        saveCropTimerRef.current = setTimeout(() => {
          if (videoId && canEdit) {
            editShortVideoService.updateShortVideo(videoId, {
              cropPositions: next,
            });
          }
        }, 800);
        return next;
      });
    },
    [videoId, aspectRatio.id, canEdit],
  );

  const [gifOverlays, setGifOverlays] = useState<GifOverlay[]>([]);
  const [textOverlays, setTextOverlays] = useState<TextOverlay[]>([]);
  const [selectedSidebarItem, setSelectedSidebarItem] =
    useState<string>("Captions");
  const [selectedElementId, setSelectedElementId] = useState<number | null>(
    null,
  );

  const socket = useWebSocket();

  // Debounced overlay save — avoids spamming the API during rapid updates (typing, dragging)
  const saveOverlaysTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const saveOverlays = useCallback(
    (
      nextGifOverlays: GifOverlay[],
      nextTextOverlays: TextOverlay[],
      debounceMs = 800,
    ) => {
      if (saveOverlaysTimerRef.current) {
        clearTimeout(saveOverlaysTimerRef.current);
      }
      saveOverlaysTimerRef.current = setTimeout(() => {
        if (videoId && shortVideo && canEdit) {
          editShortVideoService.updateShortVideo(videoId, {
            gifOverlays: nextGifOverlays,
            textOverlays: nextTextOverlays,
            videoDurationMs: shortVideo.duration_in_milliseconds,
          });
        }
      }, debounceMs);
    },
    [videoId, shortVideo, canEdit],
  );

  const handleTimeUpdate = useCallback((timeInMs: number) => {
    setCurrentTimeInMilliseconds(timeInMs);
  }, []);

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      videoControlsRef?.pause();
      setIsPlaying(false);
    } else {
      videoControlsRef?.play();
      setIsPlaying(true);
    }
  }, [isPlaying, videoControlsRef]);

  const handleSeek = useCallback(
    (timeInMs: number) => {
      if (videoControlsRef) {
        videoControlsRef.seekTo(timeInMs / 1000);
        setCurrentTimeInMilliseconds(timeInMs);
      }
    },
    [videoControlsRef],
  );

  const handleAspectRatioChange = useCallback((newAspectRatio: AspectRatio) => {
    setAspectRatio(newAspectRatio);
  }, []);

  const handleVideoEnd = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // --- Element selection ---
  const handleElementSelect = useCallback((element: IElement | null) => {
    setSelectedElementId(element?.id ?? null);
  }, []);

  // --- Bidirectional sync: timeline element time change -> update overlay ---
  const handleElementTimeChange = useCallback(
    (element: IElement) => {
      if (element.overlayId && element.type === "gif") {
        setGifOverlays((prev) => {
          const next = prev.map((g) =>
            g.id === element.overlayId
              ? {
                  ...g,
                  startMs: element.start,
                  endMs: element.start + element.width,
                }
              : g,
          );
          saveOverlays(next, textOverlays);
          return next;
        });
      }
      if (element.overlayId && element.type === "text") {
        setTextOverlays((prev) => {
          const next = prev.map((t) =>
            t.id === element.overlayId
              ? {
                  ...t,
                  startMs: element.start,
                  endMs: element.start + element.width,
                }
              : t,
          );
          saveOverlays(gifOverlays, next);
          return next;
        });
      }
    },
    [saveOverlays, gifOverlays, textOverlays],
  );

  // --- Element delete (from timeline keyboard shortcut) ---
  const handleElementDelete = useCallback(
    (element: IElement) => {
      if (element.type === "video") return;

      let nextGif = gifOverlays;
      let nextText = textOverlays;

      if (element.type === "gif" && element.overlayId) {
        nextGif = gifOverlays.filter((g) => g.id !== element.overlayId);
        setGifOverlays(nextGif);
      }
      if (element.type === "text" && element.overlayId) {
        nextText = textOverlays.filter((t) => t.id !== element.overlayId);
        setTextOverlays(nextText);
      }
      setElements((prev) => prev.filter((el) => el.id !== element.id));
      setSelectedElementId(null);
      saveOverlays(nextGif, nextText, 0);
    },
    [gifOverlays, textOverlays, saveOverlays],
  );

  // --- Element duplicate (from context menu) ---
  const handleElementDuplicate = useCallback(
    (element: IElement) => {
      if (element.type === "video") return;

      const newElementId = getNextElementId();
      const durationMs = shortVideo?.duration_in_milliseconds ?? 0;
      // Place duplicate right after the original, but keep it inside the video
      // so it never lands past the end where it can't be dragged.
      const { startMs, endMs } = clampOverlayTiming(
        element.start + element.width,
        element.start + element.width * 2,
        durationMs,
      );

      if (element.type === "gif" && element.overlayId) {
        const sourceGif = gifOverlays.find((g) => g.id === element.overlayId);
        if (sourceGif) {
          const newGifId = crypto.randomUUID();
          const newGif: GifOverlay = {
            ...sourceGif,
            id: newGifId,
            startMs,
            endMs,
          };
          setGifOverlays((prev) => [...prev, newGif]);
          setElements((prev) => {
            const nextRow =
              prev.length > 0 ? Math.max(...prev.map((el) => el.row)) + 1 : 2;
            return [
              ...prev,
              {
                ...element,
                id: newElementId,
                row: nextRow,
                start: startMs,
                width: endMs - startMs,
                overlayId: newGifId,
                isSelected: false,
                isHovered: false,
                isDragging: false,
                isResizing: false,
              },
            ];
          });
        }
      }

      if (element.type === "text" && element.overlayId) {
        const sourceText = textOverlays.find((t) => t.id === element.overlayId);
        if (sourceText) {
          const newTextId = crypto.randomUUID();
          const newText: TextOverlay = {
            ...sourceText,
            id: newTextId,
            startMs,
            endMs,
          };
          setTextOverlays((prev) => [...prev, newText]);
          setElements((prev) => {
            const nextRow =
              prev.length > 0 ? Math.max(...prev.map((el) => el.row)) + 1 : 2;
            return [
              ...prev,
              {
                ...element,
                id: newElementId,
                row: nextRow,
                start: startMs,
                width: endMs - startMs,
                overlayId: newTextId,
                isSelected: false,
                isHovered: false,
                isDragging: false,
                isResizing: false,
              },
            ];
          });
        }
      }
    },
    [getNextElementId, gifOverlays, textOverlays, shortVideo],
  );

  // --- GIF overlay handlers (ID-based mapping) ---
  const handleAddGif = useCallback(
    (gif: GifOverlay) => {
      const durationMs = shortVideo?.duration_in_milliseconds ?? 0;
      const { startMs, endMs } = clampOverlayTiming(
        gif.startMs,
        gif.endMs,
        durationMs,
      );
      const clampedGif = { ...gif, startMs, endMs };
      setGifOverlays((prev) => {
        const next = [...prev, clampedGif];
        saveOverlays(next, textOverlays, 0);
        return next;
      });
      const elementId = getNextElementId();
      setElements((prev) => {
        const nextRow =
          prev.length > 0 ? Math.max(...prev.map((el) => el.row)) + 1 : 2;
        return [
          ...prev,
          {
            id: elementId,
            row: nextRow,
            start: startMs,
            width: endMs - startMs,
            isSelected: false,
            isHovered: false,
            isDragging: false,
            isResizing: false,
            isDraggable: true,
            overlayId: clampedGif.id,
            type: "gif",
            label: clampedGif.title || "GIF",
            thumbnailUrl: clampedGif.url,
          },
        ];
      });
    },
    [getNextElementId, saveOverlays, textOverlays, shortVideo],
  );

  const handleUpdateGif = useCallback(
    (id: string, updates: Partial<GifOverlay>) => {
      setGifOverlays((prev) => {
        const next = prev.map((g) => (g.id === id ? { ...g, ...updates } : g));
        saveOverlays(next, textOverlays);
        // Sync timeline element if time range changed
        if (updates.startMs !== undefined || updates.endMs !== undefined) {
          const gif = next.find((g) => g.id === id);
          if (gif) {
            setElements((prevEls) =>
              prevEls.map((el) =>
                el.overlayId === id && el.type === "gif"
                  ? {
                      ...el,
                      start: gif.startMs,
                      width: gif.endMs - gif.startMs,
                    }
                  : el,
              ),
            );
          }
        }
        return next;
      });
    },
    [saveOverlays, textOverlays],
  );

  // --- Text overlay handlers (ID-based mapping) ---
  const handleAddText = useCallback(
    (overlay: TextOverlay) => {
      const durationMs = shortVideo?.duration_in_milliseconds ?? 0;
      const { startMs, endMs } = clampOverlayTiming(
        overlay.startMs,
        overlay.endMs,
        durationMs,
      );
      const clampedOverlay = { ...overlay, startMs, endMs };
      setTextOverlays((prev) => {
        const next = [...prev, clampedOverlay];
        saveOverlays(gifOverlays, next, 0);
        return next;
      });
      const elementId = getNextElementId();
      setElements((prev) => {
        const nextRow =
          prev.length > 0 ? Math.max(...prev.map((el) => el.row)) + 1 : 2;
        return [
          ...prev,
          {
            id: elementId,
            row: nextRow,
            start: startMs,
            width: endMs - startMs,
            isSelected: false,
            isHovered: false,
            isDragging: false,
            isResizing: false,
            isDraggable: true,
            overlayId: clampedOverlay.id,
            type: "text",
            label: clampedOverlay.text.substring(0, 20) || "Text",
          },
        ];
      });
    },
    [getNextElementId, saveOverlays, gifOverlays, shortVideo],
  );

  const handleRemoveText = useCallback(
    (id: string) => {
      setTextOverlays((prev) => {
        const next = prev.filter((t) => t.id !== id);
        saveOverlays(gifOverlays, next, 0);
        return next;
      });
      setElements((prev) =>
        prev.filter((el) => !(el.overlayId === id && el.type === "text")),
      );
      setSelectedElementId((prev) => {
        const removedEl = elements.find(
          (el) => el.overlayId === id && el.type === "text",
        );
        return removedEl && prev === removedEl.id ? null : prev;
      });
    },
    [elements, gifOverlays, saveOverlays],
  );

  const handleUpdateText = useCallback(
    (id: string, updates: Partial<TextOverlay>) => {
      setTextOverlays((prev) => {
        const next = prev.map((t) => (t.id === id ? { ...t, ...updates } : t));
        saveOverlays(gifOverlays, next);
        if (updates.startMs !== undefined || updates.endMs !== undefined) {
          const overlay = next.find((t) => t.id === id);
          if (overlay) {
            setElements((prevEls) =>
              prevEls.map((el) =>
                el.overlayId === id && el.type === "text"
                  ? {
                      ...el,
                      start: overlay.startMs,
                      width: overlay.endMs - overlay.startMs,
                    }
                  : el,
              ),
            );
          }
        }
        if (updates.text !== undefined) {
          setElements((prevEls) =>
            prevEls.map((el) =>
              el.overlayId === id && el.type === "text"
                ? { ...el, label: updates.text!.substring(0, 20) || "Text" }
                : el,
            ),
          );
        }
        return next;
      });
    },
    [saveOverlays, gifOverlays],
  );

  // WebSocket effect for export done / error events (video + audiogram)
  useEffect(() => {
    if (!socket) return undefined;

    const handleExportDone = async (data: any) => {
      setIsExporting(false);
      trackEvent("export_video_completed");

      if (data?.exported_video_url) {
        try {
          trackEvent("export_video_downloaded");
          await downloadFile(
            data.exported_video_url,
            `${data?.short_video?.title ?? "video"}.mp4`,
          );
        } catch (err) {
          console.error("Error exporting video:", err);
          setExportError(
            "Your video exported, but the download failed to start. Please try again.",
          );
        }
      }
    };

    const handleAudiogramExportDone = async (data: any) => {
      setIsExporting(false);
      trackEvent("export_audiogram_completed");

      if (data?.exported_video_url) {
        try {
          trackEvent("export_audiogram_downloaded");
          await downloadFile(
            data.exported_video_url,
            `${data?.short_video?.title ?? "video"}.mp4`,
          );
        } catch (err) {
          console.error("Error exporting audiogram:", err);
          setExportError(
            "Your audiogram exported, but the download failed to start. Please try again.",
          );
        }
      }
    };

    // Generic backend error: only surface it if an export is in flight,
    // otherwise the export backdrop could stay stuck forever.
    const handleSocketError = () => {
      if (isExportingRef.current) {
        setIsExporting(false);
        setExportError("Export failed. Please try again.");
      }
    };

    socket.on(WebSocketEventName.VIDEO_EXPORT_DONE, handleExportDone);
    socket.on(
      WebSocketEventName.AUDIOGRAM_EXPORT_DONE,
      handleAudiogramExportDone,
    );
    socket.on(WebSocketEventName.ERROR, handleSocketError);

    return () => {
      socket.off(WebSocketEventName.VIDEO_EXPORT_DONE, handleExportDone);
      socket.off(
        WebSocketEventName.AUDIOGRAM_EXPORT_DONE,
        handleAudiogramExportDone,
      );
      socket.off(WebSocketEventName.ERROR, handleSocketError);
    };
  }, [socket, downloadFile, setIsExporting, setExportError, isExportingRef]);

  // Pause video when export starts
  useEffect(() => {
    if (isExporting && isPlaying) {
      videoControlsRef?.pause();
      setIsPlaying(false);
    }
  }, [isExporting, isPlaying, videoControlsRef]);

  // Initialize elements when video loads
  useEffect(() => {
    if (shortVideo) {
      setStyles(shortVideo.subtitle_styles);
      setParagraphs(shortVideo?.paragraphs ?? []);
      setCropPositions(shortVideo.crop_positions ?? {});

      const durationMs = shortVideo.duration_in_milliseconds;

      // Restore saved GIF overlays
      const savedGifs = (shortVideo.gif_overlays ?? []).map((o) =>
        mapApiGifOverlayToFe(o, durationMs),
      );
      setGifOverlays(savedGifs);

      // Restore saved text overlays
      const savedTexts = (shortVideo.text_overlays ?? []).map((o) =>
        mapApiTextOverlayToFe(o, durationMs),
      );
      setTextOverlays(savedTexts);

      // Build timeline elements
      const videoElementId = getNextElementId();
      const gifElements = savedGifs.map((g, i) => ({
        id: getNextElementId(),
        row: 2 + i,
        start: g.startMs,
        width: g.endMs - g.startMs,
        isSelected: false,
        isHovered: false,
        isDragging: false,
        isResizing: false,
        isDraggable: true,
        overlayId: g.id,
        type: "gif" as const,
        label: g.title || "GIF",
        thumbnailUrl: g.url,
      }));
      const textElements = savedTexts.map((t, i) => ({
        id: getNextElementId(),
        row: 2 + savedGifs.length + i,
        start: t.startMs,
        width: t.endMs - t.startMs,
        isSelected: false,
        isHovered: false,
        isDragging: false,
        isResizing: false,
        isDraggable: true,
        overlayId: t.id,
        type: "text" as const,
        label: t.text.substring(0, 20) || "Text",
      }));

      setElements([
        {
          id: videoElementId,
          row: 1,
          start: 0,
          width: durationMs,
          isSelected: false,
          isHovered: false,
          isDragging: false,
          isResizing: false,
          isDraggable: false,
          type: "video",
          label: "Video",
        },
        ...gifElements,
        ...textElements,
      ]);
    }
  }, [shortVideo]);

  const backToEpisode = () =>
    navigate(episodeId ? `/episodes/${episodeId}/videos` : "/episodes");

  if (isVideoError || (!videoId && !isVideoLoading)) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          px: 3,
          textAlign: "center",
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        <VideoFileIcon sx={{ fontSize: 56, color: "text.disabled" }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Couldn&apos;t load this video
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 420 }}
        >
          The video may have been removed, or something went wrong while loading
          it. Please try again.
        </Typography>
        <Button variant="contained" onClick={backToEpisode}>
          Back to Short Videos
        </Button>
      </Box>
    );
  }

  if (isVideoLoading || !shortVideo) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading editor…
        </Typography>
      </Box>
    );
  }

  const handleUpdateStyles = async (
    updatedStyles: SubtitleStyles,
  ): Promise<void> => {
    setStyles(updatedStyles);
    if (videoId && canEdit) {
      await editShortVideoService.updateShortVideo(videoId, {
        subtitleStyles: updatedStyles,
      });
    }
  };

  const handleUpdateParagraph = async (paragraph: Paragraph): Promise<void> => {
    if (!videoId || !canEdit) return;

    const updatedParagraph = syncParagraphWordsFromText(paragraph);
    setParagraphs((prev) =>
      prev.map((p) => (p.id === updatedParagraph.id ? updatedParagraph : p)),
    );
    await editShortVideoService.updateShortVideo(videoId, {
      paragraph: updatedParagraph,
    });
  };

  if (!videoId) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <SidebarView
        pageName="Edit Short Video"
        expended={false}
        editFileSidebar={canEdit}
        hidePrimary
        ref={sidebarRef}
        selectedSidebarItem={selectedSidebarItem}
        onSelectSidebarItem={setSelectedSidebarItem}
      />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Headerbar
          pageName={videoTitle}
          onMobileMenuClick={handleMobileMenuToggle}
          headerLeftContent={
            <Breadcrumbs
              items={[
                {
                  label: "Episodes",
                  path: "/episodes",
                  icon: <PlaylistPlayIcon sx={{ fontSize: 18 }} />,
                },
                {
                  label: episodeName,
                  path: `/episodes/${episodeId}`,
                },
                {
                  label: "Short Videos",
                  path: `/episodes/${episodeId}/videos`,
                  icon: <OndemandVideoIcon sx={{ fontSize: 18 }} />,
                },
                {
                  label: videoTitle,
                  path: `/episodes/${episodeId}/videos/${videoId}/edit`,
                  icon: <EditIcon sx={{ fontSize: 18 }} />,
                },
              ]}
              showHome={false}
              inHeader
            />
          }
        />
        <Box
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flex: 1,
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          <Card
            component={Stack}
            spacing={0}
            direction="column"
            sx={{
              position: "relative",
              px: 0,
              py: 1,
              height: "100%",
              backgroundColor: (theme) => theme.palette.background.default,
              minWidth: 0,
            }}
          >
            <Backdrop
              open={isExporting}
              sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
              }}
            >
              <Paper
                elevation={24}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  backgroundColor: "#1e293b",
                  backgroundImage: "none",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  maxWidth: 400,
                  width: "90%",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress
                    size={60}
                    thickness={4}
                    sx={{
                      color: (theme) => theme.palette.primary.main,
                    }}
                  />
                  <VideoFileIcon
                    sx={{
                      position: "absolute",
                      fontSize: 24,
                      color: (theme) => theme.palette.primary.main,
                    }}
                  />
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#f1f5f9",
                      mb: 1,
                    }}
                  >
                    Exporting Video
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#94a3b8",
                      lineHeight: 1.5,
                    }}
                  >
                    Your video is being processed and exported. This may take a
                    few minutes depending on the video length and quality.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#94a3b8",
                    fontSize: "0.875rem",
                  }}
                >
                  <DownloadIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption" sx={{ color: "inherit" }}>
                    The file will download automatically when ready
                  </Typography>
                </Box>
              </Paper>
            </Backdrop>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "100%",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  maxHeight: "calc(100% - 420px)",
                  px: 2,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: "30%",
                    px: 2,
                    minWidth: 0,
                    overflow: "hidden",
                    display: canEdit ? "block" : "none",
                  }}
                >
                  {selectedSidebarItem === "Captions" && (
                    <Subtitle
                      styles={styles}
                      paragraphs={paragraphs}
                      onUpdateStyles={handleUpdateStyles}
                      onUpdateParagraph={handleUpdateParagraph}
                      currentTimeInMilliseconds={currentTimeInMilliseconds}
                      onParagraphClick={handleSeek}
                    />
                  )}
                  {selectedSidebarItem === "Text" && (
                    <Box
                      sx={{
                        background: (theme) => theme.palette.background.paper,
                        borderRadius: "18px",
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? "0 4px 24px rgba(0,0,0,0.4)"
                            : "0 4px 24px rgba(0,0,0,0.08)",
                        p: 3,
                        width: "100%",
                        m: 2,
                      }}
                    >
                      <TextOverlayPanel
                        textOverlays={textOverlays}
                        videoDurationMs={shortVideo.duration_in_milliseconds}
                        currentTimeMs={currentTimeInMilliseconds}
                        onAddText={handleAddText}
                        onRemoveText={handleRemoveText}
                        onUpdateText={handleUpdateText}
                      />
                    </Box>
                  )}
                  {selectedSidebarItem === "Elements" && (
                    <Box
                      sx={{
                        background: (theme) => theme.palette.background.paper,
                        borderRadius: "18px",
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? "0 4px 24px rgba(0,0,0,0.4)"
                            : "0 4px 24px rgba(0,0,0,0.08)",
                        p: 3,
                        width: "100%",
                        m: 2,
                      }}
                    >
                      <GifPicker
                        videoDurationMs={shortVideo.duration_in_milliseconds}
                        currentTimeMs={currentTimeInMilliseconds}
                        onAddGif={handleAddGif}
                      />
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    width: canEdit ? "70%" : "100%",
                    alignSelf: "start",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <VideoPlayer
                    videoId={videoId}
                    url={shortVideo?.url ?? ""}
                    paragraphs={paragraphs}
                    isPlaying={isPlaying}
                    toggleIsPlaying={handlePlayPause}
                    subtitleStyles={styles}
                    onTimeUpdate={handleTimeUpdate}
                    currentTimeInMilliseconds={currentTimeInMilliseconds}
                    onControlsReady={setVideoControlsRef}
                    onUpdateStyles={handleUpdateStyles}
                    aspectRatio={aspectRatio}
                    onVideoEnd={handleVideoEnd}
                    gifOverlays={gifOverlays}
                    onUpdateGif={handleUpdateGif}
                    textOverlays={textOverlays}
                    onUpdateText={handleUpdateText}
                    cropPositionX={currentCrop.x}
                    cropPositionY={currentCrop.y}
                    onCropPositionChange={handleCropPositionChange}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  width: "100%",
                  alignSelf: "center",
                }}
              >
                <Timeline
                  video={shortVideo}
                  canEdit={canEdit}
                  currentTimeInMilliseconds={currentTimeInMilliseconds}
                  isPlaying={isPlaying}
                  onPlayPause={handlePlayPause}
                  setCurrentTimeInMilliseconds={handleSeek}
                  elements={elements}
                  setElements={setElements}
                  setIsExporting={setIsExporting}
                  currentAspectRatio={aspectRatio}
                  onAspectRatioChange={handleAspectRatioChange}
                  currentSubtitleStyles={styles}
                  selectedElementId={selectedElementId}
                  onElementSelect={handleElementSelect}
                  onElementDelete={handleElementDelete}
                  onElementDuplicate={handleElementDuplicate}
                  onElementTimeChange={handleElementTimeChange}
                  gifOverlays={gifOverlays}
                  textOverlays={textOverlays}
                />
              </Box>
            </Box>
          </Card>
        </Box>
      </Box>

      <Snackbar
        open={!!exportError}
        autoHideDuration={8000}
        onClose={() => setExportError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setExportError(null)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {exportError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
